import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

export default function FounderSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section id="about" className="relative pt-20 pb-10 sm:pt-28 sm:pb-14 px-6 sm:px-12 lg:px-24" data-testid="founder-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">

          {/* Name + accent block */}
          <motion.div
            className="lg:col-span-4"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="glass-card p-8 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#3D5638]/15 rounded-full blur-3xl" />

              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-2">
                {t('founder.name')}
              </h2>
              <p className="text-sm font-mono tracking-widest uppercase text-[#BDD6B5]/70 mb-6">
                {t('founder.role')}
              </p>

              <div className="h-px w-16 bg-gradient-to-r from-[#BDD6B5]/60 to-transparent mb-6 animate-bio-pulse" />

              <a
                href="https://www.linkedin.com/in/ana-gabriela-larios"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin-link"
                className="inline-flex items-center gap-2 text-sm text-[#BDD6B5] hover:text-white transition-colors font-medium group"
              >
                <ExternalLink size={16} strokeWidth={1.5} />
                <span className="group-hover:underline underline-offset-4">{t('founder.linkedin')}</span>
              </a>
            </div>
          </motion.div>

          {/* Bio */}
          <motion.div
            className="lg:col-span-8"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="space-y-5 text-white/78 text-base leading-relaxed">
              <p>{t('founder.bio1')}</p>
              <p>{t('founder.bio2')}</p>
              <p className="text-[#BDD6B5]/70 font-medium text-sm italic">
                {t('founder.quote')}
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
